import type {
  heatmapSiteSnapshotRequestSchema,
  heatmapPageRequestSchema,
  heatmapPagesRequestSchema,
  heatmapClickedElementsRequestSchema,
  heatmapVersionsRequestSchema,
} from "schemas/bedrockApi/heatmaps";
import type * as z from "zod";
import type { RequestBlobFn, RequestFn } from "./request";

import {
  heatmapPageResponseSchema,
  heatmapPagesResponseSchema,
  heatmapClickedElementsResponseSchema,
  heatmapVersionsResponseSchema,
} from "schemas/bedrockApi/heatmaps";

export type HeatmapPageRequest = z.input<typeof heatmapPageRequestSchema>;
export type HeatmapPage = z.infer<typeof heatmapPageResponseSchema>;
export type HeatmapPagesRequest = z.infer<typeof heatmapPagesRequestSchema>;
export type HeatmapPages = z.infer<typeof heatmapPagesResponseSchema>;
export type HeatmapPageSummary = HeatmapPages["pages"][number];
export type HeatmapClickedElementsRequest = z.infer<
  typeof heatmapClickedElementsRequestSchema
>;
export type HeatmapClickedElements = z.infer<
  typeof heatmapClickedElementsResponseSchema
>;
export type HeatmapClickedElement = HeatmapClickedElements["elements"][number];
export type HeatmapVersionsRequest = z.infer<
  typeof heatmapVersionsRequestSchema
>;
export type HeatmapVersions = z.infer<typeof heatmapVersionsResponseSchema>;
export type HeatmapVersion = HeatmapVersions["versions"][number];
export type HeatmapSiteSnapshotRequest = z.infer<
  typeof heatmapSiteSnapshotRequestSchema
>;

export function createHeatmapsClient({
  request,
  requestBlob,
}: {
  request: RequestFn;
  requestBlob: RequestBlobFn;
}) {
  return {
    page: async ({
      projectId,
      ...body
    }: HeatmapPageRequest & { projectId: string }) => {
      const result = await request<unknown>({
        method: "POST",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/heatmaps/page`,
        body,
      });
      return heatmapPageResponseSchema.parse(result);
    },
    pages: async ({
      projectId,
      ...body
    }: HeatmapPagesRequest & { projectId: string }) => {
      const result = await request<unknown>({
        method: "POST",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/heatmaps/pages`,
        body,
      });
      return heatmapPagesResponseSchema.parse(result);
    },
    clickedElements: async ({
      projectId,
      ...body
    }: HeatmapClickedElementsRequest & { projectId: string }) => {
      const result = await request<unknown>({
        method: "POST",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/heatmaps/clicked-elements`,
        body,
      });
      return heatmapClickedElementsResponseSchema.parse(result);
    },
    versions: async ({
      projectId,
      ...body
    }: HeatmapVersionsRequest & { projectId: string }) => {
      const result = await request<unknown>({
        method: "POST",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/heatmaps/versions`,
        body,
      });
      return heatmapVersionsResponseSchema.parse(result);
    },
    /** A version's site snapshot: gzipped rrweb JSON, or the JSON itself when storage decoded it. */
    siteSnapshot: ({
      projectId,
      signal,
      ...body
    }: HeatmapSiteSnapshotRequest & {
      projectId: string;
      signal?: AbortSignal;
    }) => {
      return requestBlob({
        method: "POST",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/heatmaps/site-snapshot`,
        body,
        signal,
      });
    },
  };
}
